import { ROLE_GROUPS, allowedByRoles } from './roles.js';

/**
 * Multi-app workspace registry — the single source of truth for the app
 * switcher and the per-app side navigation.
 *
 * Each app:
 *   key       — stable id, used for localStorage "last visited path" keys
 *   label     — human name shown in the switcher
 *   tagline   — one-liner under the label in the switcher menu
 *   icon      — NAV_ICONS key used for the app glyph in the switcher
 *   accentVar — CSS custom property name that colors the app dot/glyph
 *               (always referenced as `var(<accentVar>)`, never a hex)
 *   items     — nav items of that app: { key, label, path, badge? }
 *               `key` doubles as the NAV_ICONS lookup, so existing keys
 *               (home, leads, deals, customers, dead, inventory, triage,
 *               compliance, channels, settings, help) must not change.
 */
export const APPS = [
  {
    key: 'sales',
    label: 'Sales',
    tagline: 'Leads, deals & follow-up',
    icon: 'leads',
    accentVar: '--accent-primary',
    items: [
      { key: 'home',         label: 'Dashboard',          path: '/' },
      // Pipeline screens — hidden from the two support-pod roles (Test Drive
      // Controller, Inventory Controller) whose jobs are the demo fleet and
      // the stock, not leads and deals. See ROLE_GROUPS.SALES_PIPELINE.
      { key: 'leads',        label: 'Lead Accelerator',   path: '/leads',        roles: ROLE_GROUPS.SALES_PIPELINE },
      { key: 'deals',        label: 'Sales Accelerator',  path: '/deals',        roles: ROLE_GROUPS.SALES_PIPELINE },
      { key: 'quotes',       label: 'Deal Desk',          path: '/quotes',       roles: ROLE_GROUPS.DEAL_DESK },
      { key: 'customers',    label: 'Customer 360',       path: '/customers',    roles: ROLE_GROUPS.SALES_PIPELINE },
      // The Test Drive Controller's two working screens — they book, check in
      // and run every drive, so Appointments and Test Drives stay visible.
      { key: 'appointments', label: 'Appointments',       path: '/appointments', roles: ROLE_GROUPS.SALES_PIPELINE_WITH_TDC },
      { key: 'testdrives',   label: 'Test Drives',        path: '/test-drives',  roles: ROLE_GROUPS.SALES_PIPELINE_WITH_TDC },
      { key: 'appraisals',   label: 'Appraisals',         path: '/appraisals',   roles: ROLE_GROUPS.SALES_PIPELINE },
      // Inventory stays open to everyone — "View inventory: Yes" in every
      // column of the client's matrix; Tasks likewise (each sees their own).
      { key: 'inventory',    label: 'Inventory',          path: '/inventory' },
      { key: 'tasks',        label: 'Tasks',              path: '/tasks' },
    ],
  },
  // ── Service app — HIDDEN FOR NOW (kept, not deleted) ────────────────────────
  // Taken out of the app switcher at the client's request. The /loan-cars and
  // /service routes, pages and backend are all still in place; to bring the
  // Service app back, un-comment this block AND the '/service' entry in
  // EXTRA_PATH_APP below.
  // {
  //   key: 'showroom',
  //   label: 'Service',
  //   tagline: 'Loan cars & courtesy vehicles',
  //   icon: 'inventory',
  //   accentVar: '--info',
  //   items: [
  //     { key: 'loancars',   label: 'Loan Cars',   path: '/loan-cars' },
  //   ],
  // },
  {
    key: 'marketing',
    label: 'Marketing',
    tagline: 'Campaigns & re-engagement',
    icon: 'campaigns',
    accentVar: '--warning',
    items: [
      { key: 'campaigns', label: 'Campaigns',  path: '/campaigns',  roles: ROLE_GROUPS.SALES_STAFF },
      { key: 'surveys',   label: 'Surveys',    path: '/surveys',    roles: ROLE_GROUPS.SALES_STAFF },
      { key: 'dead',      label: 'Lost Leads', path: '/dead-leads', roles: ROLE_GROUPS.SALES_STAFF },
    ],
  },
  {
    key: 'insights',
    label: 'Insights',
    tagline: 'Analytics & compliance',
    icon: 'analytics',
    accentVar: '--success',
    items: [
      { key: 'analytics',  label: 'Analytics',  path: '/analytics',  roles: ROLE_GROUPS.MANAGEMENT },
      { key: 'kpis',       label: 'Pilot KPIs', path: '/pilot-kpis', roles: ROLE_GROUPS.MANAGEMENT },
      { key: 'compliance', label: 'Compliance', path: '/compliance', roles: ROLE_GROUPS.COMPLIANCE },
    ],
  },
  {
    key: 'platform',
    label: 'Platform',
    tagline: 'Setup, billing & health',
    icon: 'settings',
    accentVar: '--rail-text',
    items: [
      { key: 'settings',   label: 'Settings',          path: '/settings',          roles: ROLE_GROUPS.ADMIN },
      { key: 'team',       label: 'Team',              path: '/team',              roles: ROLE_GROUPS.ADMIN },
      { key: 'onboarding', label: 'Onboarding',        path: '/onboarding',        roles: ROLE_GROUPS.ADMIN },
      { key: 'billing',    label: 'Plan & Billing',    path: '/billing',           roles: ROLE_GROUPS.ADMIN },
      { key: 'health',     label: 'Dealer Health',     path: '/health',            roles: ROLE_GROUPS.MANAGEMENT_WITH_SERVICE },
      // `badge: 4` removed. It was a literal — the only genuinely hardcoded
      // number in the nav — and it had nothing behind it: the handoff queue it
      // would have counted (GET /v1/handoffs) currently returns 500. Restore it
      // when that endpoint works, wired through badgeFor() in SideNav.jsx like
      // every other badge, never as a constant.
      { key: 'triage',     label: 'Triage & QA',       path: '/triage' },
      { key: 'pipeline',   label: 'Pipeline Settings', path: '/pipeline-settings', roles: ROLE_GROUPS.MANAGEMENT },
      { key: 'help',       label: 'Help',              path: '/help' },
    ],
  },
];

/* Legacy/detail routes that don't appear as nav items but still belong to an
   app — keeps the right nav visible on e.g. /customers/42 or /service. */
const EXTRA_PATH_APP = {
  // '/service':      'showroom',   // Service app hidden — see the APPS block above
  '/customer-360': 'sales',
  '/vehicles':     'sales',
};

/**
 * Which app owns `pathname`? Longest-prefix match over every app's item
 * paths ('/' only matches exactly), so '/leads/123' resolves to Sales.
 * Unknown paths fall back to the first app (Sales).
 */
export function appForPath(pathname) {
  const path = pathname || '/';
  let best = null;
  let bestLen = -1;
  for (const app of APPS) {
    for (const item of app.items) {
      const matches = item.path === '/'
        ? path === '/'
        : path === item.path || path.startsWith(item.path + '/');
      if (matches && item.path.length > bestLen) {
        best = app;
        bestLen = item.path.length;
      }
    }
  }
  if (best) return best;
  for (const [prefix, appKey] of Object.entries(EXTRA_PATH_APP)) {
    if (path === prefix || path.startsWith(prefix + '/')) {
      return APPS.find((a) => a.key === appKey) || APPS[0];
    }
  }
  return APPS[0];
}

/**
 * Landing path when switching into an app — the first nav item THIS USER can
 * open, not the app's first item full stop.
 *
 * The difference bit a Sales Manager: Platform's first item is Settings, which
 * is top-level only, so switching apps walked them straight onto the "no
 * access" page. An app switch should land somewhere the user is welcome; the
 * guard page is for typed URLs, not for our own navigation.
 */
export function defaultPathForApp(appKey, roles) {
  const app = APPS.find((a) => a.key === appKey);
  if (!app) return '/';
  const items = roles === undefined ? app.items : visibleItems(app, roles);
  return items[0]?.path || app.items[0]?.path || '/';
}

/* ── role-aware access ───────────────────────────────────────────────────
   One table drives four surfaces — the side nav, the app switcher, the route
   guard and the command palette. They used to disagree simply because each
   decided for itself; deriving all four from `APPS` is what stops a page being
   hidden from the nav but still reachable by typing the URL.

   An item with no `roles` key is open to any signed-in user. That mirrors the
   backend, which leaves many endpoints at `.anyRequest().authenticated()`. The
   portal deliberately does not invent restrictions the server does not make:
   hiding a page the API would happily serve is a bug, not caution.          */

/**
 * The nav item owning `pathname`, by longest-prefix match — the same rule
 * `appForPath` uses, so `/leads/123` inherits the access of `/leads`.
 */
export function itemForPath(pathname) {
  const path = pathname || '/';
  let best = null;
  let bestLen = -1;
  for (const app of APPS) {
    for (const item of app.items) {
      const matches = item.path === '/'
        ? path === '/'
        : path === item.path || path.startsWith(item.path + '/');
      if (matches && item.path.length > bestLen) {
        best = item;
        bestLen = item.path.length;
      }
    }
  }
  return best;
}

/** Roles required to open `pathname`, or null when it is unrestricted. */
export function rolesForPath(pathname) {
  return itemForPath(pathname)?.roles || null;
}

/** The items of `app` this user may see. */
export function visibleItems(app, roles) {
  return (app?.items || []).filter((i) => allowedByRoles(roles, i.roles));
}

/**
 * Apps with at least one visible item. Without this the switcher would offer
 * a role an app whose every entry is hidden from them, which opens onto an
 * empty rail.
 */
export function visibleApps(roles) {
  return APPS.filter((a) => visibleItems(a, roles).length > 0);
}
